import type { QueryClient } from '@tanstack/react-query'

import { ensureRouteData } from '@/app/query-client'
import type { MarketRange } from '@/features/market/api/marketApi'
import { liveMarketQueryOptions } from '@/features/prices/api/queries'
import {
  DEFAULT_TRADING_RANGE,
  type TradingPeriod,
} from '@/features/trading-history/lib/tradingSearch'
import { toBsDate } from '@/lib/bsDate'
import { liveMarketQuerySchema } from '@/schemas/prices'

/**
 * A trading day as the Trading Average bars' axis prints it, in BS: "27 Bhadra". The rest of
 * Market counts in BS, so this page does too, though the mock draws AD. An ISO day that does not
 * convert reads "—".
 */
export const bsDayMonth = (iso: string): string => toBsDate(iso)?.format('D MMMM', 'en') ?? '—'

/** The periods the select offers: the ranges `price-history` serves, worded as the mock words its own. */
export const PERIOD_OPTIONS: readonly { value: MarketRange; label: string }[] = [
  { value: '1W', label: 'Last week' },
  { value: '1M', label: 'Last month' },
  { value: '3M', label: 'Last 3 months' },
  { value: '1Y', label: 'Last year' },
  { value: 'ALL', label: 'All time' },
]

/** The select's value for the period on screen: a preset's range, or `custom` for a from/to link. */
export const CUSTOM_PERIOD = 'custom'

export const periodValue = (period: TradingPeriod): string =>
  period.kind === 'preset' ? period.range : CUSTOM_PERIOD

export const isMarketRange = (value: string): value is MarketRange =>
  PERIOD_OPTIONS.some((option) => option.value === value)

/** The day's busiest name, as Live Market ranks it: the company Reset returns to (a bare URL opens on it). */
const BUSIEST = liveMarketQuerySchema.parse({ sortBy: 'volume', sortDesc: true, pageSize: 10 })

/**
 * What "Reset" runs: the default period on the busiest company. `undefined` when Live Market
 * cannot say, which leaves the company as it is.
 */
export async function defaultFilters(
  client: QueryClient,
): Promise<{ symbol: string | undefined; range: MarketRange }> {
  try {
    const page = await ensureRouteData(client, liveMarketQueryOptions(BUSIEST))
    return { symbol: page.rows[0]?.symbol, range: DEFAULT_TRADING_RANGE }
  } catch {
    return { symbol: undefined, range: DEFAULT_TRADING_RANGE }
  }
}
